// src/context/ThemeContext.js
import React, { createContext, useMemo, useState, useContext } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";

const ThemeToggleContext = createContext();

export const useThemeToggle = () => useContext(ThemeToggleContext);

export const CustomThemeProvider = ({ children }) => {
  const [mode, setMode] = useState("light");

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === "light"
            ? {
                // Light theme colors
                primary: {
                  main: "#1e293b", // Dark blue-gray for text/icons
                  light: "#475569",
                  dark: "#0f172a",
                },
                secondary: {
                  main: "#64748b", // Medium gray for secondary text
                },
                background: {
                  default: "#f8fafc",
                  paper: "#ffffff",
                },
                text: {
                  primary: "#1e293b",
                  secondary: "#64748b",
                },
                sidebar: {
                  bg: "#1e293b", // Sidebar background
                  text: "#f8fafc", // Sidebar text
                },
                accent: {
                  light: "#f1f5f9", // Light hover background
                },
              }
            : {
                // Dark theme colors
                primary: {
                  main: "#f1f5f9", // Light color for text/icons
                  light: "#f8fafc",
                  dark: "#cbd5e1",
                },
                secondary: {
                  main: "#94a3b8", // Medium gray for secondary text
                },
                background: {
                  default: "#0f172a",
                  paper: "#1e293b",
                },
                text: {
                  primary: "#f1f5f9",
                  secondary: "#94a3b8",
                },
                sidebar: {
                  bg: "#0f172a", // Darker sidebar
                  text: "#f1f5f9",
                },
                accent: {
                  light: "#334155", // Dark hover background
                },
              }),
        },
        components: {
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: mode === "light" ? "#ffffff" : "#1e293b",
                color: mode === "light" ? "#1e293b" : "#f1f5f9",
                borderBottom: `2px solid ${mode === "light" ? "#e2e8f0" : "#334155"}`,
              },
            },
          },
        },
      }),
    [mode]
  );

  const toggleTheme = () => {
    setMode((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeToggleContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeToggleContext.Provider>
  );
};